import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export type EmailAlerts = Pick<Tables<"email_alert_preferences">,
  "new_article" | "changed_article" | "new_comment" | "other_change" | "email">;

export const alertCategories = [
  ["new_article", "New Article"],
  ["changed_article", "Changed Article"],
  ["new_comment", "New Comment"],
  ["other_change", "Some Other Change"],
] as const;

export const defaultEmailAlerts: EmailAlerts = {
  new_article: false, changed_article: false, new_comment: false, other_change: false, email: null,
};

export function useEmailAlerts() {
  const { user } = useAuth();
  const userId = user?.id;
  const [alerts, setAlerts] = useState<EmailAlerts>(defaultEmailAlerts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setAlerts(defaultEmailAlerts);
    const load = async () => {
      if (!userId) return;
      const { data, error } = await supabase.from("email_alert_preferences")
        .select("new_article, changed_article, new_comment, other_change, email")
        .eq("user_id", userId).maybeSingle();
      if (!active) return;
      if (error) throw error;
      setAlerts(data ?? defaultEmailAlerts);
    };
    load().catch(() => {
      if (active) setError("Email alert preferences could not be loaded. Please reload this page before saving.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId]);

  const saveAlerts = async (next: EmailAlerts) => {
    if (!user || loading || error) throw new Error(error || "Email alerts are not ready to save.");
    const { error: saveError } = await supabase.from("email_alert_preferences")
      .upsert({ user_id: user.id, ...next }, { onConflict: "user_id" });
    if (saveError) throw saveError;
    setAlerts(next);
  };

  return { alerts, loading, error, saveAlerts };
}
